import React from 'react';
import { Droplet, Calculator, TrendingUp, Ruler, Thermometer, Minus, Gauge, Activity, Wind } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета расхода воды в трубах
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  // Выбираем нужную иконку в зависимости от типа расчета
  const getCalculationTypeIcon = () => {
    switch (results.calculationType) {
      case 'flowRate':
        return <Droplet className="w-4 h-4" />;
      case 'pressure':
        return <Gauge className="w-4 h-4" />;
      case 'diameter':
        return <Ruler className="w-4 h-4" />;
      default:
        return <Calculator className="w-4 h-4" />;
    }
  };

  // Выбираем заголовок в зависимости от типа расчета
  const getCalculationTypeTitle = () => {
    switch (results.calculationType) {
      case 'flowRate':
        return 'Расчет расхода воды';
      case 'pressure':
        return 'Расчет перепада давления';
      case 'diameter':
        return 'Расчет диаметра трубы';
      default:
        return 'Результаты расчета';
    }
  };

  return (
    <ToolResult title={getCalculationTypeTitle()}>
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Основной результат расчета */}
        <div className="rounded-lg bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/30 dark:to-cyan-900/30 p-5 border border-blue-100 dark:border-blue-800/50 shadow-sm">
          <h3 className="text-base font-medium text-blue-700 dark:text-blue-300 mb-1.5 flex items-center gap-1.5">
            {getCalculationTypeIcon()}
            {results.calculationType === 'flowRate' ? 'Расход воды' : 
             results.calculationType === 'pressure' ? 'Перепад давления' : 
             'Оптимальный диаметр трубы'}
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-blue-800 dark:text-blue-200" aria-live="polite">
              {results.calculationType === 'flowRate' ? 
                `${formatNumber(results.flowRate, 2)} м³/ч` : 
               results.calculationType === 'pressure' ? 
                `${formatNumber(results.pressureDrop, 3)} бар` : 
                `${formatNumber(results.diameter * 1000, 1)} мм`}
            </span>
          </div>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
            {results.calculationType === 'flowRate' ? 
              `Скорость потока: ${formatNumber(results.velocity, 2)} м/с` : 
             results.calculationType === 'pressure' ? 
              `При расходе: ${formatNumber(results.flowRate, 2)} м³/ч` : 
              results.standardRecommendedDiameter ? 
                `Рекомендуемый стандартный диаметр: ${formatNumber(results.standardRecommendedDiameter, 1)} мм` : 
                'Расчетное значение'}
          </p>
        </div>

        {/* Режим течения */}
        <div className="rounded-lg bg-gradient-to-br from-teal-50 to-emerald-50 dark:from-teal-900/30 dark:to-emerald-900/30 p-5 border border-teal-100 dark:border-teal-800/50 shadow-sm">
          <h3 className="text-base font-medium text-teal-700 dark:text-teal-300 mb-1.5 flex items-center gap-1.5">
            <Activity className="w-4 h-4"/>
            Режим течения
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-teal-800 dark:text-teal-200" aria-live="polite">
              {results.flowType}
            </span>
          </div>
          <p className="text-xs text-teal-600 dark:text-teal-400 mt-1">
            Число Рейнольдса: {formatNumber(results.reynoldsNumber, 0)}
            {results.reynoldsNumber < 2300 ? 
              ' (Re < 2300, ламинарный поток)' : 
              results.reynoldsNumber < 4000 ? 
                ' (2300 < Re < 4000, переходный режим)' : 
                ' (Re > 4000, турбулентный поток)'}
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Внутренний диаметр трубы" 
          value={`${formatNumber(results.pipeInnerDiameter, 1)} мм`} 
          color="blue" 
          icon={<Ruler className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Длина трубы" 
          value={`${formatNumber(results.pipeLength, 1)} м`} 
          color="indigo" 
          icon={<Minus className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Материал трубы" 
          value={getPipeTypeName(results.pipeType)} 
          icon={<Minus className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Шероховатость поверхности" 
          value={`${formatNumber(results.pipeRoughness, 3)} мм`} 
          color="gray"
        />
        
        <ResultItem 
          label="Температура воды" 
          value={`${formatNumber(results.waterTemperature, 1)} °C`} 
          color="orange" 
          icon={<Thermometer className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Давление в системе" 
          value={`${formatNumber(results.systemPressure, 1)} бар`} 
          color="gray" 
          icon={<Gauge className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Скорость потока" 
          value={`${formatNumber(results.velocity, 2)} м/с`} 
          color="blue" 
          icon={<Wind className="w-4 h-4"/>} 
        />
        
        {/* Показываем специфические результаты в зависимости от типа расчета */}
        {results.calculationType === 'flowRate' && (
          <ResultItem 
            label="Объемный расход" 
            value={`${formatNumber(results.flowRate, 3)} м³/ч (${formatNumber(results.flowRate / 3600 * 1000, 2)} л/с)`} 
            color="blue" 
            icon={<Droplet className="w-4 h-4"/>} 
          />
        )}
        
        {results.calculationType === 'pressure' && (
          <ResultItem 
            label="Перепад давления" 
            value={`${formatNumber(results.pressureDrop, 3)} бар (${formatNumber(results.pressureDrop * 100, 2)} кПа)`} 
            color="red" 
            icon={<TrendingUp className="w-4 h-4"/>} 
          />
        )}
        
        {results.calculationType === 'diameter' && (
          <>
            <ResultItem 
              label="Расчетный диаметр" 
              value={`${formatNumber(results.diameter * 1000, 2)} мм`} 
              color="blue" 
              icon={<Ruler className="w-4 h-4"/>} 
            />
            {results.standardRecommendedDiameter && (
              <ResultItem 
                label="Рекомендуемый стандартный диаметр" 
                value={`${formatNumber(results.standardRecommendedDiameter, 1)} мм`} 
                color="green" 
                icon={<Ruler className="w-4 h-4"/>} 
              />
            )}
            {results.actualPressureDrop && (
              <ResultItem 
                label="Фактический перепад давления" 
                value={`${formatNumber(results.actualPressureDrop, 3)} бар`} 
                color="orange" 
                icon={<Gauge className="w-4 h-4"/>} 
              />
            )}
          </>
        )}
        
        {/* Показываем информацию о местных сопротивлениях, если они включены */}
        {results.includeLocalResistance && (
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Влияние местных сопротивлений</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600 dark:text-gray-400">
              {results.localResistanceCoefficients.bendResistance > 0 && (
                <div className="flex justify-between">
                  <span>Повороты на 90°:</span>
                  <span className="font-medium">{formatNumber(results.localResistanceCoefficients.bendResistance, 2)}</span>
                </div>
              )}
              {results.localResistanceCoefficients.valveResistance > 0 && (
                <div className="flex justify-between">
                  <span>Вентили:</span>
                  <span className="font-medium">{formatNumber(results.localResistanceCoefficients.valveResistance, 2)}</span>
                </div>
              )}
              {results.localResistanceCoefficients.teeResistance > 0 && (
                <div className="flex justify-between">
                  <span>Тройники:</span>
                  <span className="font-medium">{formatNumber(results.localResistanceCoefficients.teeResistance, 2)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span>Вход в трубу:</span>
                <span className="font-medium">{formatNumber(results.localResistanceCoefficients.entranceResistance, 2)}</span>
              </div>
              <div className="flex justify-between">
                <span>Выход из трубы:</span>
                <span className="font-medium">{formatNumber(results.localResistanceCoefficients.exitResistance, 2)}</span>
              </div>
              <div className="flex justify-between col-span-full border-t pt-1 mt-1 border-gray-200 dark:border-gray-700">
                <span className="font-medium">Суммарное сопротивление:</span>
                <span className="font-medium text-blue-600 dark:text-blue-400">{formatNumber(results.totalLocalResistance, 2)}</span>
              </div>
              </div>
          </div>
        )}
        
        {/* Показываем информацию о перепаде высот, если включено */}
        {results.includeElevationChange && (
          <div className="bg-teal-50 dark:bg-teal-900/20 rounded-lg p-4 mt-2 border border-teal-100 dark:border-teal-800">
            <h4 className="text-sm font-medium text-teal-700 dark:text-teal-300 mb-2">Перепад высот</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-teal-600 dark:text-teal-400">
              <div className="flex justify-between">
                <span>Изменение высоты:</span>
                <span className="font-medium">{formatNumber(results.elevationChange, 2)} м {results.elevationChange > 0 ? '(вверх)' : results.elevationChange < 0 ? '(вниз)' : ''}</span>
              </div>
              <div className="flex justify-between">
                <span>Гидростатический напор:</span>
                <span className="font-medium">{formatNumber(Math.abs(results.elevationChange * 0.0981), 3)} бар</span>
              </div>
              <div className="col-span-full text-xs mt-1">
                {results.elevationChange > 0 
                  ? 'Подъем воды требует дополнительной энергии и увеличивает перепад давления.'
                  : results.elevationChange < 0 
                    ? 'Спуск воды снижает требуемую энергию и уменьшает перепад давления.'
                    : 'Нет перепада высот в системе.'}
              </div>
            </div>
          </div>
        )}
        
        {/* Рекомендации и информация о безопасности */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg p-4 mt-4">
          <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-2">Анализ результатов</h4>
          <ul className="space-y-2 text-sm text-blue-600 dark:text-blue-400">
            {/* Оценка скорости потока */}
            <li className="flex gap-2">
              <span className="flex-shrink-0">•</span>
              <span>
                {results.velocity < 0.5 
                  ? 'Скорость потока слишком низкая, возможно образование отложений в трубе.' 
                  : results.velocity > 3 
                    ? 'Скорость потока высокая, возможен повышенный шум и эрозия трубы.' 
                    : 'Скорость потока находится в оптимальном диапазоне (0.5-3 м/с).'}
              </span>
            </li>
            
            {/* Оценка числа Рейнольдса */}
            <li className="flex gap-2">
              <span className="flex-shrink-0">•</span>
              <span>
                {results.flowType === 'Ламинарный' 
                  ? 'Ламинарный режим течения обеспечивает стабильный поток, но низкую теплопередачу.' 
                  : results.flowType === 'Переходный' 
                    ? 'Переходный режим течения может быть нестабильным, рекомендуется изменить параметры.' 
                    : 'Турбулентный режим течения обеспечивает хорошую теплопередачу, но более высокие потери давления.'}
              </span>
            </li>
            
            {/* Рекомендации по диаметру */}
            {results.calculationType === 'diameter' && results.standardRecommendedDiameter && (
              <li className="flex gap-2">
                <span className="flex-shrink-0">•</span>
                <span>
                  Для заданных параметров рекомендуется использовать стандартный диаметр {formatNumber(results.standardRecommendedDiameter, 1)} мм.
                </span>
              </li>
            )}
            
            {/* Предупреждение о кавитации */}
            {results.velocity > 5 && (
              <li className="flex gap-2 text-amber-600 dark:text-amber-400 font-medium">
                <span className="flex-shrink-0">⚠️</span>
                <span>
                  Внимание: высокая скорость потока может вызвать кавитацию и повреждение трубопровода!
                </span>
              </li>
            )}
          </ul>
        </div>
      </div>
    </ToolResult>
  );
};

// Вспомогательная функция для получения названия типа трубы
const getPipeTypeName = (pipeTypeId) => {
  const pipeTypeMap = {
    'steel': 'Стальная',
    'copper': 'Медная',
    'pvc': 'ПВХ (пластиковая)',
    'pp': 'Полипропиленовая',
    'pex': 'Сшитый полиэтилен (PEX)',
    'hdpe': 'ПНД (полиэтилен низкого давления)',
    'cast_iron': 'Чугунная',
    'stainless_steel': 'Нержавеющая сталь'
  };
  
  return pipeTypeMap[pipeTypeId] || pipeTypeId;
};

export default ResultsOverview;
            